/**
 * Merges the two halves of the home page — `static_home_page` (CDN-cacheable, rarely changes) and
 * `live_home_page` (live banners / live videos / live zoom / the signed-in user's recent videos) — into
 * the single response object the home page used to receive from the retired `home_page_dashboard` API.
 *
 * Merge rules (see HOME_PAGE_MERGE_RULES, first source's items come first):
 *   banner_new : static.banner_new + live.banner_new            (live banners appended)
 *   new        : live.new + live.live_zoom + static.new         (live videos, then live zoom sessions, then new)
 *   recent     : live.recent + static.recent
 *   live_zoom  : live.live_zoom + static.live_zoom
 *   everything else (playlists, tile_playlists, shorts, most_popular, ...) comes from the static response.
 *
 * Every block is a Laravel paginator payload `{ data: [], total, category_name, ... }`. Missing, null or
 * plain-array blocks on either side are tolerated so a failed or partial API never breaks the page.
 */

/** Laravel paginator block as returned by the medias API. */
export interface HomePageBlock {
  data: any[];
  total?: number;
  category_name?: string;
  type?: string;
  [key: string]: any;
}

export type HomePageSource = 'static' | 'live';

/** One contribution to a merged block: which API, which block of it, and an optional per-item adapter. */
export interface HomePageBlockSource {
  from: HomePageSource;
  block: string;
  adapt?: (item: any) => any;
}

/**
 * Shapes a zoom meeting (`id, title, slug, thumbnail, is_premium, zoom_type, status`) like a video so it can
 * sit in a video slider (`app-trailer-card`): the card needs `thumbnail_image` / `is_shorts` / `trailer_hls_url`
 * to exist, and `is_zoom` lets it route clicks to `zoom/<slug>` instead of `video/<slug>`.
 */
export function zoomMeetingToVideoCard(meeting: any): any {
  return {
    ...meeting,
    is_zoom: 1,
    is_live: 1,
    is_shorts: 0,
    trailer_hls_url: null,
    thumbnail_image: meeting.thumbnail_image || meeting.thumbnail || '',
  };
}

/** Merged block key -> ordered sources (items of the first source come first in the merged `data`). */
export const HOME_PAGE_MERGE_RULES: { [block: string]: HomePageBlockSource[] } = {
  banner_new: [
    { from: 'static', block: 'banner_new' },
    { from: 'live', block: 'banner_new' },
  ],
  new: [
    { from: 'live', block: 'new' },
    { from: 'live', block: 'live_zoom', adapt: zoomMeetingToVideoCard },
    { from: 'static', block: 'new' },
  ],
  recent: [
    { from: 'live', block: 'recent' },
    { from: 'static', block: 'recent' },
  ],
  live_zoom: [
    { from: 'live', block: 'live_zoom' },
    { from: 'static', block: 'live_zoom' },
  ],
};

/** `response` of a successful API envelope, else null. */
export function successResponse(envelope: any): any {
  return (envelope && envelope['statusCode'] === 200 && envelope['error'] === false && envelope['response'])
    ? envelope['response']
    : null;
}

/**
 * Merges the raw API envelopes of static_home_page and live_home_page.
 * Returns an envelope of the same shape (`{ statusCode, error, message, response }`) carrying the merged
 * `response`, or `null` when neither call succeeded.
 */
export function mergeHomePageResponses(staticEnvelope: any, liveEnvelope: any): any {
  const staticResponse = successResponse(staticEnvelope);
  const liveResponse = successResponse(liveEnvelope);
  if (!staticResponse && !liveResponse) {
    return null;
  }
  const envelope = staticResponse ? staticEnvelope : liveEnvelope;
  return { ...envelope, response: mergeHomePageData(staticResponse, liveResponse) };
}

/** Merges the `response` payloads (not the envelopes) of the two APIs following HOME_PAGE_MERGE_RULES. */
export function mergeHomePageData(staticData: any, liveData: any): any {
  const responses: { [source: string]: any } = { static: staticData || {}, live: liveData || {} };
  const merged = { ...responses.static };
  Object.keys(HOME_PAGE_MERGE_RULES).forEach(block => {
    merged[block] = composeBlock(block, HOME_PAGE_MERGE_RULES[block].map(source => ({
      ...source,
      part: toBlock(responses[source.from][source.block]),
    })));
  });
  return merged;
}

export interface ResolvedBlockSource extends HomePageBlockSource {
  part: HomePageBlock | null;
}

/**
 * Concatenates the available parts in order.
 *  - Duplicate ids are dropped only among items of the same source block (ids of different entities —
 *    zoom meetings vs videos — can legitimately collide).
 *  - `total` is the sum of the parts' totals minus dropped duplicates, so the sliders' "everything loaded"
 *    guards (`data.length === total`) still hold.
 *  - Metadata (category_name, type, paging info) comes from the static block of the same name when present,
 *    else the first block of the same name, else the first available part.
 * Returns null when no part is available.
 */
export function composeBlock(block: string, sources: ResolvedBlockSource[]): HomePageBlock | null {
  const available = sources.filter(source => !!source.part);
  if (available.length === 0) {
    return null;
  }
  const meta = available.find(source => source.from === 'static' && source.block === block)
    || available.find(source => source.block === block)
    || available[0];
  const seen = new Set<string>();
  const data: any[] = [];
  let total = 0;
  available.forEach(source => {
    total += blockTotal(source.part);
    source.part.data.forEach(item => {
      const id = itemId(item);
      if (id !== undefined) {
        const key = source.block + ':' + id;
        if (seen.has(key)) {
          total--;
          return;
        }
        seen.add(key);
      }
      data.push(source.adapt ? source.adapt(item) : item);
    });
  });
  return { ...meta.part, data, total };
}

/** Normalises null / plain arrays / paginator objects into a `{ data: [], ... }` block (null when absent). */
export function toBlock(value: any): HomePageBlock | null {
  if (!value) {
    return null;
  }
  if (Array.isArray(value)) {
    return { data: value, total: value.length };
  }
  if (typeof value === 'object') {
    return { ...value, data: Array.isArray(value.data) ? value.data : [] };
  }
  return null;
}

function itemId(item: any): any {
  return (item && item.id !== undefined && item.id !== null) ? item.id : undefined;
}

function blockTotal(block: HomePageBlock): number {
  return typeof block.total === 'number' ? block.total : block.data.length;
}
